/**
 * time4subs - Operations Desk
 * Main entry point
 *
 * This file shows how to wire together all the components.
 * Adjust imports based on your project structure.
 */

import React, { useState, lazy, Suspense } from "react";
import { CalendarDays, LayoutDashboard, ClipboardList, Clock3, ChartNoAxesCombined, BriefcaseBusiness, Users, Wallet, Settings as SettingsIcon, PanelLeftClose, PanelLeftOpen, LogOut, ArrowUpRight } from "lucide-react";
import { useApp } from "./lib/AppContext";
import { useAuth } from "./lib/AuthContext";

// Lazy-loaded page components for code splitting
const OperationsDesk = lazy(() => import("./pages/OperationsDesk"));
const CrewScheduler = lazy(() => import("./pages/CrewScheduler"));
const JobSetup = lazy(() => import("./pages/JobSetup"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const PayrollExport = lazy(() => import("./pages/PayrollExport"));
const Settings = lazy(() => import("./pages/Settings"));
const DailyReport = lazy(() => import("./pages/DailyReport"));
const DailyReports = lazy(() => import("./pages/DailyReports"));
const Employees = lazy(() => import("./pages/Employees"));
const MyTimesheets = lazy(() => import("./pages/MyTimesheets"));
const TimecardReview = lazy(() => import("./pages/TimecardReview"));
const Production = lazy(() => import("./pages/Production"));

export default function Time4SubsApp() {
  const [currentPage, setCurrentPage] = useState("scheduler");
  const { currentUser, organizations, organization, orgId, loadError } = useApp();
  const { signOut } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [dailyReportContext, setDailyReportContext] = useState(null); // { jobId, jobName, date }

  // Navigation structure
  const navItems = [
    { id: "operations", label: "Operations Desk", icon: LayoutDashboard },
    { id: "scheduler", label: "Crew Scheduler", icon: CalendarDays },
    { id: "daily-reports", label: "Daily Reports", icon: ClipboardList },
    { id: "timecard-review", label: "Timecard Review", icon: Clock3 },
    { id: "production", label: "Production", icon: ChartNoAxesCombined },
    { id: "jobs", label: "Jobs", icon: BriefcaseBusiness },
    { id: "employees", label: "Employees", icon: Users },
    { id: "payroll", label: "Payroll Export", icon: Wallet },
  ];

  // User info - would come from auth
  const user = currentUser
    ? {
        name: currentUser.name,
        company: orgId === "all" ? "All companies" : organizations?.find(item => item.org_id === orgId)?.organization?.name || organization?.name || "Selected company",
        initials: currentUser.initials,
      }
    : { name: "Loading...", company: "", initials: "..." };

  // pm4subs integration flag - would come from org settings
  const pm4subsEnabled = true;

  // Navigate to job detail
  const navigateToJob = (jobId) => {
    setSelectedJobId(jobId);
    setCurrentPage("job-detail");
  };

  // Navigate to daily report
  const navigateToDailyReport = (jobId, jobName, date) => {
    setDailyReportContext(previous => ({ jobId, jobName, date,
      returnPage: currentPage === "daily-report" ? previous?.returnPage || "daily-reports" : currentPage,
    }));
    setCurrentPage("daily-report");
  };

  // Render current page
  const renderPage = () => {
    switch (currentPage) {
      case "operations":
        return <OperationsDesk onViewDailyReport={navigateToDailyReport} />;
      case "scheduler":
        return (
          <CrewScheduler
            onViewDailyReport={navigateToDailyReport}
            onNavigateToJob={navigateToJob}
          />
        );
      case "daily-reports":
        return <DailyReports onSelectReport={navigateToDailyReport} />;
      case "timecard-review":
        return (
          <TimecardReview
            onNavigateToPayroll={() => setCurrentPage("payroll")}
          />
        );
      case "production":
        return <Production />;
      case "jobs":
        return <JobSetup onSelectJob={navigateToJob} />;
      case "employees":
        return <Employees />;
      case "job-detail":
        return (
          <JobDetail
            jobId={selectedJobId}
            onBack={() => setCurrentPage("jobs")}
          />
        );
      case "payroll":
        return <PayrollExport />;
      case "my-timesheets":
        return <MyTimesheets onSelectReport={navigateToDailyReport} />;
      case "settings":
        return <Settings />;
      case "daily-report":
        return (
          <DailyReport
            jobId={dailyReportContext?.jobId}
            jobName={dailyReportContext?.jobName}
            date={dailyReportContext?.date}
            onBack={() => setCurrentPage(dailyReportContext.returnPage || "daily-reports")}
            onDateChange={(newDate) =>
              setDailyReportContext((prev) => ({ ...prev, date: newDate }))
            }
          />
        );
      default:
        return <OperationsDesk />;
    }
  };

  return (
    <div className={`ops-shell ${sidebarCollapsed ? "nav-collapsed" : ""}`}>
      <aside className="ops-navigation" aria-label="Main navigation">
        <div className="ops-brand"><span className="ops-brand-mark">4</span><div className="ops-nav-label"><strong>time4subs</strong><small>OPERATIONS DESK</small></div></div>
        <button className="ops-nav-toggle" onClick={() => setSidebarCollapsed(value => !value)} aria-label={sidebarCollapsed ? "Expand navigation" : "Collapse navigation"} aria-expanded={!sidebarCollapsed}>
          {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}<span className="ops-nav-label">Collapse menu</span>
        </button>
        <nav>{navItems.map(item => {
          const active = currentPage === item.id || (currentPage === "job-detail" && item.id === "jobs");
          const Icon = item.icon;
          return <button key={item.id} onClick={() => setCurrentPage(item.id)} title={item.label} aria-label={item.label} aria-current={active ? "page" : undefined} className={active ? "is-active" : ""}>
            <Icon size={18} /><span className="ops-nav-label">{item.label}</span>
          </button>;
        })}</nav>
        <div className="ops-nav-footer">
          {pm4subsEnabled && <a href="https://pm4subs.com" target="_blank" rel="noopener noreferrer" title="Open Command Center"><ArrowUpRight size={18} /><span className="ops-nav-label">Command Center</span></a>}
          <button onClick={() => setCurrentPage("settings")} title="Settings" aria-label="Settings" aria-current={currentPage === "settings" ? "page" : undefined}><SettingsIcon size={18} /><span className="ops-nav-label">Settings</span></button>
          <div className="ops-account"><span className="ops-avatar">{user.initials}</span><div className="ops-nav-label"><strong>{user.name}</strong><small title={user.company}>{user.company}</small></div></div>
          <button onClick={() => { signOut().catch(() => {}); }} title="Sign out" aria-label="Sign out"><LogOut size={18} /><span className="ops-nav-label">Sign out</span></button>
        </div>
      </aside>
      <div role="main" className="ops-content">
        {loadError && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              padding: "12px 16px",
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            Failed to load data: {loadError}. Try refreshing the page.
          </div>
        )}
        <Suspense fallback={<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8" }}>Loading...</div>}>
          {renderPage()}
        </Suspense>
      </div>
    </div>
  );
}
